import {z} from 'zod';
import {portSchema} from './port';
import {nodeMetadataSchema} from './node';
import {EXECUTION_STATUSES} from '../n8n-api-types';
import {workflowUserVariableDeclarationSchema} from './workflow-variable-declaration';
import {
  agentWorkflowDefinitionSchema,
  saveWorkflowDraftResponseSchema,
  workflowDraftSchema,
} from './workflow-draft';
import {
  agenticWorkflowIdSchema,
  workflowDraftIdSchema,
  workflowDraftMutationIdSchema,
  workflowVersionIdSchema,
} from './agentic-workflow-identifiers';

export {
  workflowUserVariableDeclarationSchema,
  workflowUserVariableTypeSchema,
} from './workflow-variable-declaration';
export type {
  WorkflowUserVariableDeclaration,
  WorkflowUserVariableType,
} from './workflow-variable-declaration';

/**
 * Agent list item from API (GET /api/site/:site/workflows).
 * Minimal data for displaying in a list.
 */
export const agentListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  isActive: z.boolean(),
  templateSlug: z.string(),
});

export type AgentListItem = z.infer<typeof agentListItemSchema>;

export const templateCategorySchema = z.enum([
  'AEO',
  'SEO',
  'Campaigns',
  'Testing',
]);
export type TemplateCategory = z.infer<typeof templateCategorySchema>;

/**
 * Webflow-specific template metadata fields: customer-facing copy plus the
 * user-editable variables the template needs at run time.
 */
export const workflowWebflowMetadataSchema = z.object({
  category: templateCategorySchema.optional(),
  description: z.string().optional(),
  howItWorks: z.string().optional(),
  variables: z.array(workflowUserVariableDeclarationSchema).default([]),
});
export type WorkflowWebflowMetadata = z.infer<
  typeof workflowWebflowMetadataSchema
>;

/**
 * Template metadata for the get-started cards (GET …/workflows/templates).
 */
export const workflowTemplateMetadataSchema = z.object({
  slug: z.string(),
  name: z.string(),
  category: templateCategorySchema,
  description: z.string(),
});
export type WorkflowTemplateMetadata = z.infer<
  typeof workflowTemplateMetadataSchema
>;

/**
 * Agent details from API (GET /api/site/:site/workflows/:workflowId).
 * Full agent data including workflow definition and the template's declared
 * user variables (used by the Run-now form).
 */
export const agentDetailsSchema = z.object({
  id: z.string(),
  name: z.string(),
  siteId: z.string(),
  activeVersionId: workflowVersionIdSchema.nullable(),
  workflowDefinition: z.any(),
  isHidden: z.boolean(),
  isActive: z.boolean(),
  createdOn: z.string(),
  updatedOn: z.string(),
  variableDeclarations: z
    .array(workflowUserVariableDeclarationSchema)
    .default([]),
  description: z.string().optional(),
  howItWorks: z.string().optional(),
});

export type AgentDetails = z.infer<typeof agentDetailsSchema>;

/**
 * View mode for the Agents top bar.
 */
export const agentsViewModeSchema = z.enum(['logic', 'runs']);
export type AgentsViewMode = z.infer<typeof agentsViewModeSchema>;

/**
 * Selected node data for the node-info panel.
 */
export const selectedNodeDataSchema = z.object({
  id: z.string(),
  label: z.string(),
  type: z.string(),
  capability: z.string(),
  icon: z.string().optional(),
  description: z.string().optional(),
  state: z.string(),
  config: z.record(z.unknown()),
  inputs: z.array(portSchema),
  outputs: z.array(portSchema),
  metadata: nodeMetadataSchema,
  executionInputData: z.array(z.record(z.unknown())).optional(),
  executionOutputData: z.array(z.record(z.unknown())).optional(),
  executionError: z.string().optional(),
  executionTimeMs: z.number().optional(),
});
export type SelectedNodeData = z.infer<typeof selectedNodeDataSchema>;

/**
 * Base schema for node execution state shared by all node execution states.
 */
const nodeExecutionBaseSchema = z.object({
  executionTimeMs: z.number().optional(),
  outputData: z.array(z.record(z.unknown())).optional(),
  inputData: z.array(z.record(z.unknown())).optional(),
  error: z.string().optional(),
});

/**
 * Schema for per-node execution state for loops stored in the Flux store.
 */
export const nodeIterationStateSchema = nodeExecutionBaseSchema.extend({
  state: z.enum(['completed', 'error']),
  iterationIndex: z.number().optional(),
});

export const nodeExecutionStateSchema = nodeExecutionBaseSchema.extend({
  nodeId: z.string(),
  nodeName: z.string(),
  state: z.enum(['idle', 'running', 'completed', 'error']),
  iterations: z.array(nodeIterationStateSchema).optional(),
  isFinalOutput: z.boolean().optional(),
});

export const workflowRunSchema = z.object({
  id: z.string(),
  timestamp: z.date(),
  durationMs: z.number(),
  status: z.enum(EXECUTION_STATUSES),
  errorMessage: z.string().optional(),
  mode: z.string().nullable().optional(),
  startedAt: z.string().nullable().optional(),
  stoppedAt: z.string().nullable().optional(),
  /**
   * What kicked off this run. `null`/missing means manual (panel "Run"
   * button). Non-null values are AgenticWorkflowTrigger types — e.g.
   * `form_submission`, `collection_item_published`.
   */
  triggerSource: z.string().nullable().optional(),
  resolvedVariables: z
    .object({
      system: z.record(z.string()).default({}),
      variables: z
        .record(z.union([z.string(), z.array(z.string())]))
        .default({}),
    })
    .optional(),
});

export type WorkflowRun = z.infer<typeof workflowRunSchema>;

/**
 * The minimum data needed to seed the Build-view chat after the Agent
 * Composer creates an empty agent: the created agent plus the prompt the
 * user submitted.
 */
export const chatSeedSchema = z.object({
  agentId: z.string(),
  prompt: z.string(),
});

export type ChatSeed = z.infer<typeof chatSeedSchema>;

export const workflowDraftLoadStateSchema = z.discriminatedUnion('status', [
  z.object({
    draftId: workflowDraftIdSchema,
    status: z.literal('loading'),
    error: z.null(),
  }),
  z.object({
    draftId: workflowDraftIdSchema,
    status: z.literal('fresh'),
    error: z.null(),
  }),
  z.object({
    draftId: workflowDraftIdSchema,
    status: z.literal('stale'),
    error: z.string(),
  }),
  z.object({
    draftId: workflowDraftIdSchema,
    status: z.literal('forbidden'),
    error: z.string(),
  }),
]);

export const workflowDraftSaveStateSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('idle'),
    workflowId: z.null(),
    draftId: z.null(),
    mutationId: z.null(),
    response: z.null(),
    httpStatus: z.null(),
    error: z.null(),
    errorEnum: z.null(),
    draftCleared: z.null(),
  }),
  z.object({
    status: z.literal('saving'),
    workflowId: z.string(),
    draftId: workflowDraftIdSchema,
    mutationId: workflowDraftMutationIdSchema,
    response: z.null(),
    httpStatus: z.null(),
    error: z.null(),
    errorEnum: z.null(),
    draftCleared: z.null(),
  }),
  z.object({
    status: z.literal('succeeded'),
    workflowId: z.string(),
    draftId: workflowDraftIdSchema,
    mutationId: workflowDraftMutationIdSchema,
    response: saveWorkflowDraftResponseSchema,
    httpStatus: z.literal(200),
    error: z.null(),
    errorEnum: z.null(),
    draftCleared: z.boolean(),
  }),
  z.object({
    status: z.literal('failed'),
    workflowId: z.string(),
    draftId: workflowDraftIdSchema,
    mutationId: workflowDraftMutationIdSchema,
    response: z.null(),
    httpStatus: z.number().int().nullable(),
    error: z.string(),
    errorEnum: z.string().nullable(),
    draftCleared: z.null(),
  }),
]);

export type WorkflowDraftSaveState = z.infer<
  typeof workflowDraftSaveStateSchema
>;

const workflowVersionListItemStateSchema = z.object({
  versionId: workflowVersionIdSchema,
  isActiveVersion: z.boolean(),
  notes: z.string().nullable(),
  savedOn: z.string(),
  savedByUserId: z.string(),
  sourceDraftRevision: z.number().int().positive(),
});

const workflowVersionDetailsStateSchema =
  workflowVersionListItemStateSchema.extend({
    definition: agentWorkflowDefinitionSchema,
  });

export const workflowVersionListStateSchema = z.object({
  versions: z.array(workflowVersionListItemStateSchema),
  publishedVersion: workflowVersionListItemStateSchema.nullable(),
  hasMore: z.boolean(),
  isLoading: z.boolean(),
  isLoadingMore: z.boolean(),
  error: z.string().nullable(),
});

export type WorkflowVersionListState = z.infer<
  typeof workflowVersionListStateSchema
>;

export const workflowVersionLoadStateSchema = z.object({
  versionId: workflowVersionIdSchema,
  status: z.enum(['loading', 'fresh', 'stale']),
  error: z.string().nullable(),
});

export type WorkflowVersionLoadState = z.infer<
  typeof workflowVersionLoadStateSchema
>;

const workflowVersionSwitchResponseStateSchema = z.object({
  workflowId: agenticWorkflowIdSchema,
  activeVersionId: workflowVersionIdSchema,
  previousActiveVersionId: workflowVersionIdSchema,
  status: z.literal('active'),
  switchedOn: z.string(),
  switchedByUserId: z.string(),
});

export const workflowVersionSwitchStateSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('idle'),
    workflowId: z.null(),
    targetVersionId: z.null(),
    expectedActiveVersionId: z.null(),
    mutationId: z.null(),
    response: z.null(),
    httpStatus: z.null(),
    error: z.null(),
    errorEnum: z.null(),
  }),
  z.object({
    status: z.literal('switching'),
    workflowId: z.string(),
    targetVersionId: workflowVersionIdSchema,
    expectedActiveVersionId: workflowVersionIdSchema,
    mutationId: workflowDraftMutationIdSchema,
    response: z.null(),
    httpStatus: z.null(),
    error: z.null(),
    errorEnum: z.null(),
  }),
  z.object({
    status: z.literal('succeeded'),
    workflowId: z.string(),
    targetVersionId: workflowVersionIdSchema,
    expectedActiveVersionId: workflowVersionIdSchema,
    mutationId: workflowDraftMutationIdSchema,
    response: workflowVersionSwitchResponseStateSchema,
    httpStatus: z.literal(200),
    error: z.null(),
    errorEnum: z.null(),
  }),
  z.object({
    status: z.literal('failed'),
    workflowId: z.string(),
    targetVersionId: workflowVersionIdSchema,
    expectedActiveVersionId: workflowVersionIdSchema,
    mutationId: workflowDraftMutationIdSchema,
    response: z.null(),
    httpStatus: z.number().int().nullable(),
    error: z.string(),
    errorEnum: z.string().nullable(),
  }),
]);

export type WorkflowVersionSwitchState = z.infer<
  typeof workflowVersionSwitchStateSchema
>;

/**
 * Flux store state for Agents.
 */
export const agentsStateSchema = z.object({
  agentList: z.array(agentListItemSchema),
  selectedAgentId: z.string().nullable(),
  selectedAgentDetails: agentDetailsSchema.nullable(),
  selectedDraftIdByWorkflowId: z.record(workflowDraftIdSchema),
  workflowDraftByWorkflowId: z.record(workflowDraftSchema),
  workflowDraftLoadStateByWorkflowId: z.record(workflowDraftLoadStateSchema),
  workflowVersionListByWorkflowId: z.record(workflowVersionListStateSchema),
  selectedVersionIdByWorkflowId: z.record(workflowVersionIdSchema),
  workflowVersionByWorkflowId: z.record(workflowVersionDetailsStateSchema),
  workflowVersionLoadStateByWorkflowId: z.record(
    workflowVersionLoadStateSchema
  ),
  workflowVersionSwitchState: workflowVersionSwitchStateSchema,
  unavailableDraftWorkflowId: z.string().nullable(),
  isDiscardingDraft: z.boolean(),
  workflowDraftDiscardMutationId: workflowDraftMutationIdSchema.nullable(),
  workflowDraftDiscardError: z.string().nullable(),
  workflowDraftDiscardErrorStatus: z.number().int().nullable(),
  workflowDraftSaveState: workflowDraftSaveStateSchema,
  isLoading: z.boolean(),
  isLoadingDetails: z.boolean(),
  isCreating: z.boolean(),
  isDeleting: z.boolean(),
  isExecuting: z.boolean(),
  error: z.string().nullable(),
  viewMode: agentsViewModeSchema,
  isEnabled: z.boolean(),
  selectedNodeData: selectedNodeDataSchema.nullable(),
  currentExecutionId: z.string().nullable(),
  executionStatus: z.enum(EXECUTION_STATUSES).nullable(),
  nodeExecutionStates: z.array(nodeExecutionStateSchema),
  workflowRuns: z.array(workflowRunSchema),
  hasResolvedRuns: z.boolean(),
  isLoadingRuns: z.boolean(),
  isLoadingMoreRuns: z.boolean(),
  hasMoreRuns: z.boolean(),
  runsError: z.string().nullable(),
  selectedRunId: z.string().nullable(),
  selectedRunNodeStates: z.array(nodeExecutionStateSchema),
  isLoadingRunDetail: z.boolean(),
  isOverviewOpen: z.boolean(),
  isSetInputsOpen: z.boolean(),
  isChatDockDismissed: z.boolean(),
  queuedRunId: z.string().nullable(),
  // Seeds the Build-view chat once the composer's agent is created. Built
  // from the create request's meta (see createEmptyAgentWorkflow in
  // RequestStore.ts), not shared reducer state.
  composerChatSeed: chatSeedSchema.nullable(),
});

export type AgentsState = z.infer<typeof agentsStateSchema>;

/**
 * Sentinel ID used for the "Get started with agents" onboarding view.
 */
export const GET_STARTED_AGENT_ID = 'get-started' as const;

/**
 * Available workflow template slugs from the Cloudflare config.
 */
export enum WorkflowTemplateSlug {
  AEO_CONTENT_TOPIC = 'aeo-content-topic',
  AEO_CONTENT_BRIEF = 'aeo-content-brief',
  AEO_CONTENT_CREATION = 'aeo-content-creation',
  CONTENT_TOPIC_DISCOVERY = 'content-topic-discovery',
  CONTENT_RECOMMENDATION_CREATION = 'content-recommendation-creation',
  COMPETITOR_INSIGHTS_REPORT = 'competitor-insights-report',
  FIBONACCI_HAMMER = 'fibonacci-hammer',
  LLM_SMOKE = 'llm-smoke',
  SLACK_MESSAGE_LOCAL = 'slack-message-local',
}

/**
 * Shape of a single workflow template as hosted on Cloudflare/S3.
 * Uses loose typing for nodes/connections since the full n8n types
 * are only needed when loading into the canvas or server-side.
 *
 * The optional `webflow` block declares user-editable variables the
 * template expects (see `workflowWebflowMetadataSchema`).
 */
export type WorkflowTemplate = {
  name: string;
  versionId: string;
  nodes: unknown[];
  connections: Record<string, unknown>;
  settings?: Record<string, unknown>;
  webflow?: WorkflowWebflowMetadata;
};

/**
 * Shape of the `dc-devpl-3668-agentic-workflow-config` Statsig dynamic config.
 */
export type AgenticWorkflowConfig = {
  templates: Partial<Record<string, WorkflowTemplate>>;
};
